/**
 * PortalStatusIndicator — compact, server-authoritative portal-access status chip.
 *
 * Derives state entirely from existing canonical client fields:
 *   portal_status       — "none" | "invitation_sent" | "active" | "disabled" | "archived"
 *   portal_invited_at   — ISO timestamp; set when invitation is dispatched
 *   portal_activated_at — ISO timestamp; set when client first activates via invite link
 *   portal_last_login_at — ISO timestamp; updated on every successful portal /auth/login
 *
 * Four display states:
 *   not_invited — no invitation has been sent (gray/neutral)
 *   invited     — invitation sent, no successful login yet (amber/warning)
 *   active      — at least one confirmed successful login (green/success)
 *   disabled    — portal access explicitly disabled or archived (muted red)
 *
 * IMPORTANT: "active" requires portal_last_login_at or portal_status=="active".
 * A pending invitation, welcome message, or portal user record creation alone does NOT count.
 */

import { ShieldOff, ShieldAlert, ShieldCheck, ShieldX, Loader2 } from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

// ---- Status derivation -------------------------------------------------------

export function derivePortalStatus(client) {
  const ps = (client?.portal_status || "none").toLowerCase();
  if (ps === "disabled" || ps === "archived") return "disabled";
  // portal_status=="active" is canonical proof of a successful login.
  // portal_last_login_at is the per-login fallback for edge-case backfill.
  if (ps === "active" || client?.portal_last_login_at) return "active";
  if (ps === "invitation_sent" || client?.portal_invited_at) return "invited";
  return "not_invited";
}

// ---- Visual config -----------------------------------------------------------

const STATUS_META = {
  not_invited: {
    label: "Not Invited",
    Icon: ShieldOff,
    chip: "bg-slate-100 text-slate-500 border-slate-200",
    icon: "text-slate-400",
  },
  invited: {
    label: "Invited",
    Icon: ShieldAlert,
    chip: "bg-amber-50 text-amber-700 border-amber-200",
    icon: "text-amber-500",
  },
  active: {
    label: "Active",
    Icon: ShieldCheck,
    chip: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: "text-emerald-600",
  },
  disabled: {
    label: "Disabled",
    Icon: ShieldX,
    chip: "bg-red-50 text-red-600 border-red-200",
    icon: "text-red-400",
  },
};

// ---- Date formatter ----------------------------------------------------------

function fmt(iso) {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short", day: "numeric", year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// ---- Tooltip body ------------------------------------------------------------

function TooltipBody({ status, client }) {
  if (status === "not_invited") {
    return <p className="text-xs">No portal invitation has been sent.</p>;
  }
  if (status === "invited") {
    return (
      <div className="text-xs space-y-0.5">
        {client.portal_invited_at && <p>Invited: {fmtDate(client.portal_invited_at)}</p>}
        <p className="text-amber-300">Never logged in</p>
      </div>
    );
  }
  if (status === "active") {
    return (
      <div className="text-xs space-y-0.5">
        {client.portal_last_login_at && <p>Last login: {fmt(client.portal_last_login_at)}</p>}
        {client.portal_activated_at && <p className="text-slate-300">First login: {fmtDate(client.portal_activated_at)}</p>}
      </div>
    );
  }
  if (status === "disabled") {
    return (
      <div className="text-xs space-y-0.5">
        <p className="text-red-300">Portal access is disabled</p>
        {client.portal_last_login_at
          ? <p className="text-slate-300">Last login: {fmtDate(client.portal_last_login_at)}</p>
          : <p className="text-slate-400">Never logged in</p>}
      </div>
    );
  }
  return null;
}

// ---- Main component ----------------------------------------------------------

/**
 * @param {object}   client          — canonical client record (from workspace or CRM endpoint)
 * @param {function} onPortalAction  — optional: called when chip is clicked; receives status string
 * @param {boolean}  busy            — show loading spinner (while invite/resend is in flight)
 */
export function PortalStatusIndicator({ client, onPortalAction, compact = false, busy = false }) {
  const status = derivePortalStatus(client);
  const meta = STATUS_META[status];
  const { Icon } = meta;

  const isClickable = typeof onPortalAction === "function";

  const chip = (
    <button
      type="button"
      data-testid={`portal-status-indicator-${status}`}
      onClick={isClickable ? () => onPortalAction(status) : undefined}
      disabled={!isClickable}
      aria-label={`Portal: ${meta.label}`}
      className={[
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold leading-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-brand-gold",
        "transition-opacity",
        isClickable ? "cursor-pointer hover:opacity-80" : "cursor-default",
        busy ? "opacity-60" : "",
        meta.chip,
      ].join(" ")}
    >
      {busy
        ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
        : <Icon className={`h-3 w-3 shrink-0 ${meta.icon}`} aria-hidden="true" />
      }
      <span className={compact ? "sr-only sm:not-sr-only" : undefined}>
        Portal: {meta.label}
      </span>
    </button>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{chip}</TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[200px]">
          <TooltipBody status={status} client={client} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
